import { useEffect, useState } from 'react';
import type { Progress } from '../../progress';
import type { PublicMission } from '../../types';
import { useLocale } from '../../state/useLocale';
import { efficiencyLandingCopy } from '../../lib/strings/efficiency-landing';
import { savingsCopy } from '../../lib/strings/savings';
import { intlTag } from '../../lib/format';
import { fetchPreviewMeasurement, previewCompleted, type PreviewMeasurement } from '../../lib/preview-savings';
import { Button, LinkButton } from '../../ui';

export function PreviewResults({ missions, progress, userId, upgradeHref = '/?view=course#pricing' }: {
  missions: PublicMission[]; progress: Progress; userId: string | null; upgradeHref?: string;
}) {
  const { locale } = useLocale();
  const s = efficiencyLandingCopy[locale];
  const complete = previewCompleted(progress);
  const [measurement, setMeasurement] = useState<PreviewMeasurement | null>(null);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let live = true;
    setMeasurement(null);
    setLoading(Boolean(userId && complete));
    if (userId && complete) void fetchPreviewMeasurement().then((m) => { if (live) { setMeasurement(m); setLoading(false); } });
    return () => { live = false; };
  }, [userId, complete, refresh]);
  if (!complete || !missions.some((m) => m.locked)) return null;
  const usd = (micro: number) => new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(micro / 1e6);
  const delta = measurement && measurement.afterMicro !== null ? measurement.beforeMicro - measurement.afterMicro : null;
  return <section className="preview-results" aria-labelledby="preview-results-title">
    <p className="label">{s.previewTitle}</p><h2 id="preview-results-title">{s.previewBody}</h2>
    {loading ? <p role="status">{s.loading}</p> : measurement ? <>
      <h3>{s.personalTitle}</h3>
      <p className="note">{savingsCopy[locale].priced}</p>
      <dl><div><dt>{s.personalBefore}</dt><dd>{usd(measurement.beforeMicro)}</dd></div>
        {measurement.afterMicro !== null && <div><dt>{s.personalAfter}</dt><dd>{usd(measurement.afterMicro)}</dd></div>}</dl>
      {delta !== null ? <p className="cost-practice__delta">{delta > 0 ? s.less : delta < 0 ? s.more : s.equal}{delta === 0 ? '' : ': ' + usd(Math.abs(delta))}</p> : <p>{s.pending}</p>}
      {measurement.afterMicro !== null && <p className="note">{s.personalNote}</p>}{measurement.reported && <p className="note">{s.reported}</p>}
    </> : <p>{s.noMeasurement}</p>}
    <div className="savings__actions"><LinkButton href={upgradeHref}>{s.continue}</LinkButton><a href="/agent">{s.connect}</a><a href="/efficiency#right-size-model">{s.methods}</a>
      {userId && <Button variant="quiet" onClick={() => setRefresh((n) => n + 1)} disabled={loading}>{s.refresh}</Button>}</div>
  </section>;
}
